import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarClock, Mail, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import { neveroverpayApi } from './neveroverpayApi';
import { Reveal, relativeTime } from './npUi';

const HOURS = Array.from({ length: 24 }, (_, h) => [h, `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'} UTC`]);

export default function WeeklyDigestPanel({ gmail, onNeedGmailSend, showPreview = true }) {
  const [settings, setSettings] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);

  const load = useCallback(async () => {
    const [prefs, view] = await Promise.all([
      neveroverpayApi.digestSettings(),
      showPreview ? neveroverpayApi.digestPreview().catch(() => null) : Promise.resolve(null),
    ]);
    setSettings(prefs);
    setPreview(view);
  }, [showPreview]);

  useEffect(() => { load().catch((err) => setNote({ tone: 'rose', text: err.message })); }, [load]);

  async function save(patch) {
    setBusy(true);
    try {
      const row = await neveroverpayApi.saveDigestSettings(patch);
      setSettings((prev) => ({ ...(prev || {}), ...row, recent: prev?.recent || [] }));
      setNote({ tone: 'green', text: 'Saved. Your digest schedule is updated.' });
    } catch (err) {
      setNote({ tone: 'rose', text: err.message });
    } finally {
      setBusy(false);
    }
  }

  async function sendNow() {
    setBusy(true);
    setNote(null);
    try {
      const row = await neveroverpayApi.digestSendNow();
      setNote({ tone: 'green', text: `Sent "${row.subject}" to ${row.to} from your own Gmail.` });
      await load();
    } catch (err) {
      setNote({ tone: 'rose', text: err.message });
    } finally {
      setBusy(false);
    }
  }

  const delivery = settings?.delivery || {};
  const ready = Boolean(delivery.ready);
  const digest = preview?.digest;

  return (
    <div style={{ display: 'grid', gap: 16 }} data-testid="weekly-digest-panel">
      <Reveal className="np-card">
        <div className="np-between">
          <div className="np-row">
            <div className="np-icon-box" style={{ marginBottom: 0 }}><CalendarClock size={18} /></div>
            <div>
              <h3 style={{ margin: 0 }}>Weekly Money Digest</h3>
              <p className="np-muted" style={{ margin: '2px 0 0' }}>
                One friendly email a week: what came back, what is still out with companies, and what needs your OK. Sent from your own Gmail to {settings?.to_email || 'you'}.
              </p>
            </div>
          </div>
          <button
            type="button"
            className={`np-switch ${settings?.enabled ? 'on' : ''}`}
            aria-label="Toggle weekly digest"
            disabled={busy || !settings}
            onClick={() => save({ enabled: !settings?.enabled })}
            data-testid="digest-toggle"
          />
        </div>

        <div className="np-digest-controls" style={{ marginTop: 18 }}>
          <label className="np-muted">
            Day
            <select className="np-select" value={settings?.weekday ?? 0} disabled={busy || !settings} onChange={(e) => save({ weekday: Number(e.target.value) })} data-testid="digest-weekday">
              {(settings?.weekdays || []).map((day, index) => <option key={day} value={index}>{day}</option>)}
            </select>
          </label>
          <label className="np-muted">
            Time
            <select className="np-select" value={settings?.hour ?? 9} disabled={busy || !settings} onChange={(e) => save({ hour: Number(e.target.value) })} data-testid="digest-hour">
              {HOURS.map(([h, label]) => <option key={h} value={h}>{label}</option>)}
            </select>
          </label>
          <div className="np-muted">
            Delivery
            <div className={`np-card np-soft`} style={{ padding: '10px 12px', marginTop: 4, fontSize: 13 }}>
              {ready ? (
                <span className="np-row" style={{ color: '#c7f2b0' }}><CheckCircle2 size={15} /> Ready via {delivery.email || 'your Gmail'}</span>
              ) : delivery.connected ? (
                <span className="np-row" style={{ color: '#f7dc98' }}><AlertTriangle size={15} /> Allow Gmail sending to deliver</span>
              ) : (
                <span className="np-row"><Mail size={15} /> Connect Gmail to deliver</span>
              )}
            </div>
          </div>
        </div>

        <div className="np-actions">
          <button type="button" className="np-btn np-btn-icon" disabled={busy || !ready} onClick={sendNow} data-testid="digest-send-now">
            <Send size={15} /> Send me this week’s digest now
          </button>
          {!ready && delivery.connected && onNeedGmailSend ? (
            <button type="button" className="np-btn ghost" disabled={busy} onClick={onNeedGmailSend}>Allow Gmail sending</button>
          ) : null}
          {settings?.last_sent_at ? <span className="np-muted" style={{ alignSelf: 'center' }}>Last sent {relativeTime(settings.last_sent_at)}</span> : null}
        </div>
        {note ? (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={note.tone === 'rose' ? 'np-error' : 'np-muted'} style={{ marginTop: 12, color: note.tone === 'green' ? '#c7f2b0' : undefined }}>
            {note.text}
          </motion.p>
        ) : null}
      </Reveal>

      {showPreview && digest ? (
        <Reveal className="np-card" delay={0.05}>
          <div className="np-between" style={{ marginBottom: 12 }}>
            <div>
              <div className="np-kicker">This week’s preview</div>
              <h3 style={{ margin: '4px 0 0' }}>{preview.subject}</h3>
            </div>
            <span className="np-tag">{digest.week_start} → {digest.week_end}</span>
          </div>
          <div className="np-grid-4" style={{ marginBottom: 14 }}>
            {[
              ['Back in bank this week', digest.recovered_week_display, 'np-money'],
              ['Waiting on your OK', digest.needs_you_total_display, ''],
              ['Out with companies', digest.pending_total_display, ''],
              ['Renewals in 14 days', String(digest.renewals?.length || 0), ''],
            ].map(([label, value, cls]) => (
              <div key={label} className="np-card np-soft" style={{ padding: 14 }}>
                <div className="np-muted">{label}</div>
                <div className={`np-stat ${cls}`} style={{ fontSize: 22 }}>{value}</div>
              </div>
            ))}
          </div>
          <div className="np-digest-preview">
            <iframe title="Weekly digest preview" srcDoc={preview.html} sandbox="" data-testid="digest-preview-frame" />
          </div>
        </Reveal>
      ) : null}
    </div>
  );
}
